import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Coins } from "lucide-react";
import { PulseLoader } from "react-spinners";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../../../redux/app/hooks";
import { issueCashbackThunk } from "../../../../redux/features/cashbackSlice";

interface PresetBuyer {
  id: string;
  fullName?: string;
  email?: string;
}

interface PresetOrder {
  id: string;
  orderAmount: number | string;
  createdAt?: string;
  paymentRef?: string;
}

interface IssueCashbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Buyer the cashback is being credited to. Required — cashback is buyer-scoped. */
  buyer: PresetBuyer;
  /** The completed order the cashback is based on. Required — cashback is one-per-order. */
  order: PresetOrder;
}

const PERCENT_MIN = 0.1;
const PERCENT_MAX = 100;

const formatNaira = (amount: number) =>
  new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    maximumFractionDigits: 2,
  }).format(Number.isFinite(amount) ? amount : 0);

const toNumber = (raw: unknown): number => {
  if (typeof raw === "number") return raw;
  if (typeof raw === "string") {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const shortOrderRef = (order: PresetOrder): string => {
  if (order.paymentRef && order.paymentRef.length > 0) return order.paymentRef;
  return order.id.slice(0, 8).toUpperCase();
};

const ReadOnlyField: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div>
    <label className="block text-sm font-medium mb-1 text-gray-700">
      {label}
    </label>
    <input
      type="text"
      readOnly
      value={value}
      className="w-full border rounded px-3 py-2 text-sm bg-gray-50 text-gray-600"
    />
  </div>
);

/**
 * Issue Cashback to a buyer's wallet, scoped to a single completed order.
 *
 * This modal is intentionally narrow: it always requires a `buyer` AND an
 * `order`. The admin only enters the percentage. Entry points (order detail
 * page, orders list row action) already know both — there is no "ad-hoc"
 * issuance flow because the API requires `orderId`.
 *
 * Backend enforces one cashback per order. The 400 response message
 * ("Cashback has already been issued for this order.") flows through the
 * existing toast on rejection.
 */
const IssueCashbackModal: React.FC<IssueCashbackModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  buyer,
  order,
}) => {
  const dispatch = useAppDispatch();
  const { issuing } = useAppSelector((state) => state.cashback);

  const [percentage, setPercentage] = useState<string>("");
  const [error, setError] = useState<string>("");

  const orderAmount = toNumber(order.orderAmount);
  const buyerLabel =
    buyer.fullName ||
    buyer.email ||
    buyer.id;
  const orderLabel = `Order ${shortOrderRef(order)} · ${formatNaira(
    orderAmount
  )}${
    order.createdAt
      ? ` · ${moment(order.createdAt).format("Do MMM YYYY")}`
      : ""
  }`;

  // Wipe transient state on every close so reopening for a different order
  // never starts with a stale percentage or error.
  useEffect(() => {
    if (!isOpen) {
      setPercentage("");
      setError("");
    }
  }, [isOpen]);

  const validatePercentage = (raw: string): string => {
    if (!raw.trim()) return "Percentage is required.";
    const value = Number(raw);
    if (Number.isNaN(value)) return "Enter a valid number.";
    if (value < PERCENT_MIN)
      return `Percentage must be at least ${PERCENT_MIN}%.`;
    if (value > PERCENT_MAX)
      return `Percentage cannot exceed ${PERCENT_MAX}%.`;
    if (!/^\d+(\.\d{1})?$/.test(raw))
      return "Use up to one decimal place (e.g. 7.5).";
    return "";
  };

  const handlePercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPercentage(value);
    if (error) setError(validatePercentage(value));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validatePercentage(percentage);
    if (validationError) {
      setError(validationError);
      return;
    }

    const result = await dispatch(
      issueCashbackThunk({
        buyerId: buyer.id,
        percentage: Number(percentage),
        orderId: order.id,
      })
    );

    if (issueCashbackThunk.fulfilled.match(result)) {
      const credited = result.payload?.data?.cashbackAmount;
      toast.success(
        credited !== undefined
          ? `${formatNaira(credited)} cashback issued to ${buyerLabel}.`
          : `Cashback issued to ${buyerLabel}.`
      );
      onSuccess?.();
      onClose();
    } else {
      // Surfaces backend messages like "Cashback has already been issued
      // for this order." cleanly to the admin.
      toast.error((result.payload as string) || "Failed to issue cashback.");
    }
  };

  if (!isOpen) return null;

  const previewAmount = percentage.trim()
    ? (orderAmount * Number(percentage)) / 100
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="issue-cashback-title"
        className="flex max-h-[min(92dvh,720px)] w-full flex-col overflow-y-auto rounded-t-2xl bg-white p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] shadow-lg sm:max-h-[min(90vh,720px)] sm:max-w-md sm:rounded-2xl sm:p-6 sm:pb-[max(1.5rem,env(safe-area-inset-bottom))]"
      >
        <div className="flex justify-center mb-4">
          <div className="bg-lightPurple text-accent rounded-full p-4">
            <Coins size={28} />
          </div>
        </div>
        <h2
          id="issue-cashback-title"
          className="text-center text-xl font-semibold mb-2"
        >
          Issue Cashback
        </h2>
        <p className="text-center text-gray-600 mb-6 text-sm">
          A cashback worth this percentage of the order will be credited to
          the buyer's wallet immediately.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <ReadOnlyField label="Buyer" value={buyerLabel} />
          <ReadOnlyField label="Source order" value={orderLabel} />

          <div>
            <label
              htmlFor="cashback-percentage"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Cashback Percentage <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                id="cashback-percentage"
                type="number"
                inputMode="decimal"
                step="0.1"
                min={PERCENT_MIN}
                max={PERCENT_MAX}
                value={percentage}
                onChange={handlePercentageChange}
                placeholder="e.g. 1"
                disabled={issuing}
                autoFocus
                className={`w-full border rounded-md py-2.5 pl-3 pr-10 focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-50 disabled:text-gray-500 ${
                  error
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-300"
                }`}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">
                %
              </span>
            </div>
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
            <p className="text-xs text-gray-500 mt-2">
              Allowed range: {PERCENT_MIN}% – {PERCENT_MAX}%.
            </p>
          </div>

          {previewAmount !== null && (
            <div className="rounded-md border border-primary/30 bg-lightPurple/30 px-3 py-2 text-sm">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-gray-700">Cashback amount</span>
                <span className="font-semibold text-accent">
                  {formatNaira(previewAmount)}
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {percentage}% of {formatNaira(orderAmount)}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:justify-stretch">
            <button
              type="button"
              onClick={onClose}
              disabled={issuing}
              className="w-full rounded-md bg-gray-100 py-2.5 px-4 text-gray-800 hover:bg-gray-200 disabled:opacity-50 sm:flex-1"
            >
              Discard
            </button>
            <button
              type="submit"
              disabled={issuing}
              className="flex w-full items-center justify-center rounded-md bg-primary py-2.5 px-4 text-white hover:bg-primaryHover disabled:opacity-50 sm:flex-1"
            >
              {issuing ? (
                <PulseLoader size={6} color="#fff" />
              ) : (
                "Issue Cashback"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IssueCashbackModal;
